import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { eventFormSchema, eventTypeLabels, eventTypes, type EventType as EventTypeName } from '@greasemap/validation';
import { defaultCentre } from '../../events/defaultCentre';
import { useEvent } from '../../events/queries';
import { useSubmitEvent, useUpdateEvent } from '../../events/mutations';
import type { SaveEventBody } from '../../events/types';
import { LocationPickerMap } from '../../events/map/LocationPickerMap';
import {
  Button,
  Card,
  ErrorState,
  Heading,
  Select,
  Spinner,
  Stack,
  Text,
  TextArea,
  TextField,
} from '../../ui';

const TYPE_OPTIONS = eventTypes.map((value) => ({
  value,
  label: eventTypeLabels[value as EventTypeName],
}));

const { lat: defaultLat, lon: defaultLon } = defaultCentre();

const DEFAULT_VIEW = { latitude: defaultLat, longitude: defaultLon };

interface FormState {
  title: string;
  description: string;
  type: string;
  address: string;
  latitude: string;
  longitude: string;
  startsAt: string;
  endsAt: string;
}

const EMPTY: FormState = {
  title: '',
  description: '',
  type: 'CarsAndCoffee',
  address: '',
  latitude: '',
  longitude: '',
  startsAt: '',
  endsAt: '',
};

/** Formats an ISO instant as the local value a `datetime-local` input expects. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Converts a local `datetime-local` value to an ISO instant. */
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

/**
 * Submits a new event, or edits one the caller owns.
 *
 * The location is chosen by tapping the map, which fills the latitude and longitude; when the map is
 * unavailable those fields can be typed directly. Validation is the shared Zod schema, so this form
 * rejects exactly what the mobile form rejects. A new submission lands in moderation, which the form
 * states plainly on success.
 *
 * @returns The submit/edit screen.
 */
export function SubmitEventPage(): React.JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const isEdit = eventId !== undefined;
  const navigate = useNavigate();

  const existing = useEvent(isEdit ? eventId : '');
  const create = useSubmitEvent();
  const update = useUpdateEvent(eventId ?? '');

  const [values, setValues] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  // Seed the form from the server exactly once per event.
  if (isEdit && existing.data !== undefined && loadedId !== existing.data.id) {
    const loaded = existing.data;
    setLoadedId(loaded.id);
    setValues({
      title: loaded.title,
      description: loaded.description ?? '',
      type: loaded.type,
      address: loaded.address,
      latitude: String(loaded.latitude),
      longitude: String(loaded.longitude),
      startsAt: toLocalInput(loaded.startsAt),
      endsAt: loaded.endsAt == null ? '' : toLocalInput(loaded.endsAt),
    });
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    const parsed = eventFormSchema.safeParse({
      title: values.title,
      description: values.description,
      type: values.type,
      address: values.address,
      latitude: values.latitude === '' ? Number.NaN : Number(values.latitude),
      longitude: values.longitude === '' ? Number.NaN : Number(values.longitude),
      startsAt: values.startsAt,
      endsAt: values.endsAt,
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        next[key] ??= issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});

    const body: SaveEventBody = {
      title: parsed.data.title,
      description: parsed.data.description ?? null,
      type: parsed.data.type,
      address: parsed.data.address,
      latitude: parsed.data.latitude,
      longitude: parsed.data.longitude,
      startsAt: fromLocalInput(parsed.data.startsAt),
      endsAt: parsed.data.endsAt === undefined ? null : fromLocalInput(parsed.data.endsAt),
      version: isEdit ? (existing.data?.version ?? null) : null,
    };

    try {
      const saved = isEdit ? await update.mutateAsync(body) : await create.mutateAsync(body);
      await navigate(`/events/${saved.id}`);
    } catch (cause) {
      setSubmitError(cause instanceof Error ? cause.message : 'Could not save that event.');
    }
  }

  if (isEdit && existing.isPending) {
    return (
      <Stack gap="md" align="center">
        <Spinner label="Loading the event" />
        <Text muted>Loading…</Text>
      </Stack>
    );
  }

  if (isEdit && existing.isError) {
    return <ErrorState message={existing.error.message} onRetry={() => void existing.refetch()} />;
  }

  const chosen =
    values.latitude !== '' && values.longitude !== ''
      ? { latitude: Number(values.latitude), longitude: Number(values.longitude) }
      : null;

  return (
    <Stack gap="xl">
      <Heading level={1}>{isEdit ? 'Edit event' : 'Submit an event'}</Heading>

      {!isEdit && (
        <Text muted>
          New listings are reviewed before they appear on the map. You will see it marked pending
          until a moderator approves it.
        </Text>
      )}

      <Card ariaLabel="Event details">
        <form onSubmit={(event) => void submit(event)}>
          <Stack gap="lg">
            <TextField
              label="Title"
              value={values.title}
              onChange={(event) => setValues({ ...values, title: event.target.value })}
              error={errors['title']}
            />
            <Select
              label="Type"
              options={TYPE_OPTIONS}
              value={values.type}
              onChange={(event) => setValues({ ...values, type: event.target.value })}
            />
            <TextField
              label="Address"
              value={values.address}
              onChange={(event) => setValues({ ...values, address: event.target.value })}
              error={errors['address']}
            />

            <LocationPickerMap
              value={chosen}
              initialView={chosen ?? DEFAULT_VIEW}
              onPick={(latitude, longitude) =>
                setValues((current) => ({
                  ...current,
                  latitude: latitude.toFixed(6),
                  longitude: longitude.toFixed(6),
                }))
              }
            />

            <Stack direction="row" gap="lg" wrap>
              <TextField
                label="Latitude"
                inputMode="decimal"
                value={values.latitude}
                onChange={(event) => setValues({ ...values, latitude: event.target.value })}
                error={errors['latitude']}
              />
              <TextField
                label="Longitude"
                inputMode="decimal"
                value={values.longitude}
                onChange={(event) => setValues({ ...values, longitude: event.target.value })}
                error={errors['longitude']}
              />
            </Stack>

            <Stack direction="row" gap="lg" wrap>
              <TextField
                label="Starts"
                type="datetime-local"
                value={values.startsAt}
                onChange={(event) => setValues({ ...values, startsAt: event.target.value })}
                error={errors['startsAt']}
              />
              <TextField
                label="Ends (optional)"
                type="datetime-local"
                value={values.endsAt}
                onChange={(event) => setValues({ ...values, endsAt: event.target.value })}
                error={errors['endsAt']}
              />
            </Stack>

            <TextArea
              label="Description"
              value={values.description}
              onChange={(event) => setValues({ ...values, description: event.target.value })}
            />

            {submitError !== null && (
              <p className="gm-field-error" role="alert">
                {submitError}
              </p>
            )}

            <Stack direction="row" gap="sm" wrap>
              <Button type="submit" variant="primary" disabled={create.isPending || update.isPending}>
                {isEdit ? 'Save changes' : 'Submit for review'}
              </Button>
              <Button
                type="button"
                onClick={() => void navigate(isEdit ? `/events/${eventId}` : '/events')}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}
